import _ from 'lodash'
import * as yup from 'yup'

/** Permission schemas for the role form */
export const tablePermissionsFormSchema = yup.object().shape({
  addRow: yup.boolean().required(),
  deleteRow: yup.boolean().required(),
  configureTabs: yup.boolean().required(),
  configurePivotTables: yup.boolean().required(),
})

const columnPermissionsSchema = yup
  .object()
  .shape({
    view: yup.boolean().required(),
    edit: yup.boolean().required(),
  })
  .test('edit-requires-view', 'Edit permission requires view permission', function (value) {
    if (value.edit && !value.view) {
      const { tableEntityMap } = this.options.context || {}

      const pathParts = this.path.split('.')
      const columnId = pathParts[pathParts.length - 2] // Second to last is column ID

      const columnInfo = tableEntityMap?.get?.(columnId)
      const columnName = columnInfo?.name || 'Unknown column'
      const tableName = columnInfo?.tableName || 'Unknown table'
      return this.createError({
        message: `Edit permission requires view permission for column "${columnName}" in table "${tableName}"`,
      })
    }
    return true
  })

export const tabPermissionsSchema = yup.object().shape({
  view: yup.boolean().required(),
})

const formPermissionsSchema = yup.object().shape({
  submit: yup.boolean().required(),
})

const workspacePermissionsSchema = yup
  .object()
  .shape({
    view: yup.boolean().required(),
    edit: yup.boolean().required(),
  })
  .test('edit-requires-view', 'Edit permission requires view permission', function (value) {
    if (value.edit && !value.view) {
      return this.createError({
        message: `Edit permission requires view permission for workspace`,
      })
    }
    return true
  })

const manualTriggerPermissionsSchema = yup.object().shape({
  submit: yup.boolean().required(),
})

const embedPermissionsSchema = yup.object().shape({
  view: yup.boolean().required(),
})

const endpointPermissionsSchema = yup.object().shape({
  call: yup.boolean().required(),
})

// Child table schema - uses objects keyed by ID
const childTablePermissionFormSchema = yup.object().shape({
  // ColumnPermissions is an object where keys are column IDs
  columnPermissions: yup.lazy((obj) =>
    yup
      .object()
      .shape(_.mapValues(obj, () => columnPermissionsSchema.required()))
      .required(),
  ),
  // TabPermissions is an object where keys are tab IDs
  tabPermissions: yup.lazy((obj) =>
    yup
      .object()
      .shape(_.mapValues(obj, () => tabPermissionsSchema.required()))
      .required(),
  ),
})

export const tableFullPermissionFormSchema = yup
  .object()
  .shape({
    tablePermissions: tablePermissionsFormSchema.required(),
    // ColumnPermissions is an object where keys are column IDs
    columnPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => columnPermissionsSchema.required()))
        .required(),
    ),
    // TabPermissions is an object where keys are tab IDs
    tabPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => tabPermissionsSchema.required()))
        .required(),
    ),
    // ChildTables is an object where keys are child table IDs
    childTables: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => childTablePermissionFormSchema.required()))
        .required(),
    ),
  })
  .test(
    'manage-tabs-requires-all-view',
    'Managing tabs requires view permission for all tabs in this table and child tables',
    function (value) {
      // If configureTabs is not true, no need to validate further
      if (!value?.tablePermissions?.configureTabs) {
        return true
      }

      const tabsWithoutView: string[] = []
      const childTabsWithoutView: string[] = []
      for (const tabId in value.tabPermissions) {
        const tabPerm = value.tabPermissions[tabId]
        if (!tabPerm || !tabPerm.view) {
          tabsWithoutView.push(tabId)
        }
      }

      for (const childTableId in value.childTables) {
        const childTablePerm = value.childTables[childTableId]
        for (const tabId in childTablePerm.tabPermissions) {
          const tabPerm = childTablePerm.tabPermissions[tabId]
          if (!tabPerm || !tabPerm.view) {
            childTabsWithoutView.push(tabId)
          }
        }
      }

      // If all tabs and child table tabs have view permission, no need to create error
      if (tabsWithoutView.length == 0 && childTabsWithoutView.length == 0) {
        return true
      }

      const { tableEntityMap } = this.options.context || {}

      const tabNames = tabsWithoutView
        ?.map((tabId) => {
          const tabInfo = tableEntityMap?.get?.(tabId)
          const tabName = tabInfo?.name || 'Unknown tab'
          const tableName = tabInfo?.tableName || 'Unknown table'
          return `"${tabName}" in table "${tableName}"`
        })
        .join(', ')

      const errorMessage = `Managing tabs requires view permission for these tabs: ${tabNames}`

      const childTabNames = childTabsWithoutView
        ?.map((tabId) => {
          const tabInfo = tableEntityMap?.get?.(tabId)
          const tabName = tabInfo?.name || 'Unknown tab'
          const tableName = tabInfo?.tableName || 'Unknown table'
          return `"${tabName}" in table "${tableName}"`
        })
        .join(', ')

      const childErrorMessage = `Managing tabs requires view permission for these tabs in child tables: ${childTabNames}`

      return this.createError({
        message: `${errorMessage}\n${childErrorMessage}`,
        path: 'tablePermissions.configureTabs',
      })
    },
  )

// Main schema - everything keyed by ID using yup.lazy
export const structuredRoleSchema = yup
  .object()
  .shape({
    name: yup
      .string()
      .required('Role name is required')
      .min(1, 'Role name cannot be empty')
      .test('unique-name', 'Role name already exists', function (value) {
        const existingNames = this.options.context?.existingRoleNames || []
        return !existingNames.includes(value?.toLowerCase().trim())
      })
      .test('empty-name', 'Role name cannot be empty', function (value) {
        return value?.trim() !== ''
      }),
    // TablePermissions is an object where keys are table IDs
    tablePermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => tableFullPermissionFormSchema.required()))
        .required(),
    ),
    // FormPermissions is an object where keys are form IDs
    formPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => formPermissionsSchema.required()))
        .required(),
    ),
    // WorkspacePermissions is an object where keys are workspace IDs
    workspacePermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => workspacePermissionsSchema.required()))
        .required(),
    ),
    // ManualTriggerPermissions is an object where keys are trigger IDs
    manualTriggerPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => manualTriggerPermissionsSchema.required()))
        .required(),
    ),
    // EmbedPermissions is an object where keys are embed (dynamic app) IDs
    embedPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => embedPermissionsSchema.required()))
        .required(),
    ),
    // EndpointPermissions is an object where keys are endpoint IDs
    endpointPermissions: yup.lazy((obj) =>
      yup
        .object()
        .shape(_.mapValues(obj, () => endpointPermissionsSchema.required()))
        .required(),
    ),
  })
  .required()
  .test('has-permissions', 'At least one permission is required', function (value) {
    if (
      !value ||
      !value.tablePermissions ||
      !value.formPermissions ||
      !value.workspacePermissions ||
      !value.manualTriggerPermissions ||
      !value.embedPermissions ||
      !value.endpointPermissions
    ) {
      return false
    }

    // Check table permissions
    const hasTablePermission = Object.values(value.tablePermissions).some((tablePerms) => {
      if (!tablePerms) {
        return false
      }

      const hasTablePerms = Object.values(tablePerms.tablePermissions || {}).some(Boolean)
      const hasColumnPerms = Object.values(tablePerms.columnPermissions || {}).some(
        (colPermissions) => colPermissions?.view || colPermissions?.edit,
      )
      const hasTabPerms = Object.values(tablePerms.tabPermissions || {}).some(
        (tabPermissions) => tabPermissions?.view,
      )
      return hasTablePerms || hasColumnPerms || hasTabPerms
    })

    // Check form permissions
    const hasFormPermission = Object.values(value.formPermissions).some(
      (formPerms) => formPerms?.submit === true,
    )

    // Check workspace permissions
    const hasWorkspacePermission = Object.values(value.workspacePermissions).some(
      (workspacePerms) => workspacePerms?.view === true || workspacePerms?.edit === true,
    )

    // Check manual trigger permissions
    const hasManualTriggerPermission = Object.values(value.manualTriggerPermissions).some(
      (triggerPerms) => triggerPerms?.submit === true,
    )

    // Check embed (dynamic app) permissions
    const hasEmbedPermission = Object.values(value.embedPermissions).some(
      (embedPerms) => embedPerms?.view === true,
    )

    // Check endpoint permissions
    const hasEndpointPermission = Object.values(value.endpointPermissions).some(
      (endpointPerms) => endpointPerms?.call === true,
    )

    const isValid =
      hasTablePermission ||
      hasFormPermission ||
      hasWorkspacePermission ||
      hasManualTriggerPermission ||
      hasEmbedPermission ||
      hasEndpointPermission

    if (!isValid) {
      return this.createError({
        message: 'At least one permission must be enabled',
        path: 'permissions',
      })
    }

    return true
  })

// Manual type definitions for better TypeScript support with yup.lazy
export type ColumnPermissionsFormData = {
  view: boolean
  edit: boolean
}

export type TabPermissionsFormData = {
  view: boolean
}

export type TablePermissionsFormData = {
  addRow: boolean
  deleteRow: boolean
  configureTabs: boolean
  configurePivotTables: boolean
}

export type FormPermissionsFormData = {
  submit: boolean
}

export type WorkspacePermissionsFormData = {
  view: boolean
  edit: boolean
}

export type ManualTriggerPermissionsFormData = {
  submit: boolean
}

export type EmbedPermissionsFormData = {
  view: boolean
}

export type EndpointPermissionsFormData = {
  call: boolean
}

export type ChildTablePermissionsFormData = {
  columnPermissions: Record<string, ColumnPermissionsFormData>
  tabPermissions: Record<string, TabPermissionsFormData>
}

export type TableFullPermissionFormData = {
  tablePermissions: TablePermissionsFormData
  columnPermissions: Record<string, ColumnPermissionsFormData>
  tabPermissions: Record<string, TabPermissionsFormData>
  childTables: Record<string, ChildTablePermissionsFormData>
}

export type StructuredRoleFormData = {
  name: string
  tablePermissions: Record<string, TableFullPermissionFormData>
  formPermissions: Record<string, FormPermissionsFormData>
  workspacePermissions: Record<string, WorkspacePermissionsFormData>
  manualTriggerPermissions: Record<string, ManualTriggerPermissionsFormData>
  embedPermissions: Record<string, EmbedPermissionsFormData>
  endpointPermissions: Record<string, EndpointPermissionsFormData>
}
